import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import L from 'leaflet'
import { createLogger } from '../utils/logger'

const logger = createLogger('usePopupCoordinator')

// Utility: Get panel element
const getPanelElement = () => {
  // Find bottom-sheet or top-bar panels — but NOT full-height sidebars.
  // The new desktop layout uses a left sidebar (fixed top-0 left-0 bottom-0)
  // whose bg-white child spans the full viewport height. Treating it as a
  // "top panel" yields topPadding ≈ viewportHeight + 110 ≈ 1000px, which
  // causes a catastrophic pan that sends markers off-screen.
  const candidates = document.querySelectorAll(
    '.fixed.bottom-0 [class*="bg-white"], ' + // Bottom-sheet panel content
    '[class*="rounded-t-2xl"][class*="bg-white"], ' + // Drag bar area
    '[class*="rounded-2xl shadow"]' // Last resort
  )
  for (const el of candidates) {
    const rect = el.getBoundingClientRect()
    // Skip full-height elements — these are sidebars, not bottom sheets or top bars
    if (rect.height > window.innerHeight * 0.85) continue
    return el
  }
  return null
}

// Utility: Calculate top padding for popup positioning within visible map area.
// For bottom-sheet layout the popup should appear in the upper portion of the
// visible map (above the sheet), so we return a fixed offset from the top.
const calculateTopPadding = (panelElement, defaultPadding = 120) => {
  if (!panelElement) return defaultPadding
  const rect = panelElement.getBoundingClientRect()
  // Bottom-sheet: panel sits at the bottom half of the screen
  if (rect.top > window.innerHeight * 0.5) {
    return 120 // Popup appears in upper portion of visible map
  }
  // Legacy top-panel: popup should appear below the panel
  return rect.bottom + 110
}

/**
 * usePopupCoordinator - Manages popup lifecycle with panel state awareness
 * 
 * Coordinates marker popup opening with panel state transitions to ensure
 * popups always use correct padding constraints. Follows atomic, idempotent,
 * and modular architecture principles.
 * 
 * @param {Object} options
 * @param {string} options.panelState - Current panel state (minimized/semi/expanded)
 * @param {Function} options.onPanelStateChange - Callback to request panel state change
 * @returns {Object} Popup coordination utilities
 * 
 * @example
 * const { openPopup, closePopup, popupPadding } = usePopupCoordinator({
 *   panelState,
 *   onPanelStateChange: setPanelState
 * })
 */
export function usePopupCoordinator({ panelState, onPanelStateChange }) {
  const [activePopup, setActivePopup] = useState(null)
  const [pendingPopup, setPendingPopup] = useState(null)
  const resizeObserverRef = useRef(null)
  const panelStateRef = useRef(panelState)
  
  // Keep ref in sync with prop
  useEffect(() => {
    panelStateRef.current = panelState
  }, [panelState])
  
  /**
   * Calculate popup padding based on current panel state
   * Memoized to prevent unnecessary recalculations
   */
  // Bottom-sheet layout: panel height goes into bottomRight padding so Leaflet
  // auto-pans to keep popups above the sheet. topLeft uses a small fixed top
  // offset for the nav bar only.
  const popupPadding = useMemo(() => ({
    topLeft: [20, 80], // left=20, top=80 (nav bar clearance — fixed)
    bottomRight: [
      20,
      panelState === 'minimized' ? 80 : panelState === 'semi' ? 180 : 420 // bottom = sheet height
    ]
  }), [panelState])
  
  /**
   * Open popup with proper panel state coordination
   * 
   * @param {Object} marker - Leaflet marker reference
   * @param {Object} options - Configuration options
   * @param {boolean} options.requireMinimizedPanel - Whether popup requires minimized panel
   */
  const openPopup = useCallback((marker, options = {}) => {
    const { requireMinimizedPanel = true } = options
    
    // Close any existing popup first
    if (activePopup && activePopup !== marker) {
      activePopup.closePopup()
      setActivePopup(null)
    }
    
    // Clear any pending popup
    if (pendingPopup && pendingPopup.marker !== marker) {
      setPendingPopup(null)
    }
    
    // Check if panel needs to be minimized
    if (requireMinimizedPanel && panelState !== 'minimized') {
      setPendingPopup({ marker, options })
      onPanelStateChange?.('minimized')
      return
    }
    
    // Panel is already in correct state - open after brief delay to let previous popup close
    
    // Calculate exact position where popup should appear
    const panel = getPanelElement()
    const topPadding = calculateTopPadding(panel)
    const popup = marker.getPopup()
    
    if (popup) {
      popup.options.autoPanPaddingTopLeft = L.point(20, topPadding)
      popup.options.autoPanPaddingBottomRight = L.point(20, 20)
      popup.options.keepInView = true
      popup.options.autoPan = true
    }
    
    // Use setTimeout to ensure previous popup is fully closed
    setTimeout(() => {
      marker.openPopup()
      setActivePopup(marker)
      setPendingPopup(null)
      
      // Wait for popup to fully render, then position at exact distance using actual DOM position
      setTimeout(() => {
        if (!marker._map) return
        
        const map = marker._map
        const popupElement = popup?.getElement()
        
        if (popupElement) {
          // Use actual rendered position from DOM (not calculated from marker)
          const popupRect = popupElement.getBoundingClientRect()
          const currentPopupTop = popupRect.top
          
          // Calculate pan needed to reach exact target position
          const panDelta = currentPopupTop - topPadding
          
          if (Math.abs(panDelta) > 5) { // Only pan if difference is significant
            const currentCenter = map.getCenter()
            const currentCenterPoint = map.latLngToContainerPoint(currentCenter)
            const newCenterPoint = L.point(currentCenterPoint.x, currentCenterPoint.y + panDelta)
            const newCenter = map.containerPointToLatLng(newCenterPoint)
            
            map.panTo(newCenter, { animate: true, duration: 0.25 })
          }
        }
      }, 100) // Wait for popup to fully render
    }, 10)
  }, [panelState, onPanelStateChange, activePopup, pendingPopup])
  
  /**
   * Effect: Open pending popup when panel minimization is detected
   * Uses ref to access current panel state, avoiding stale closure issues
   */
  useEffect(() => {
    const currentPanelState = panelStateRef.current
    
    if (!pendingPopup) {
      return
    }
    
    // Check if React state already shows minimized
    if (currentPanelState === 'minimized') {
      const panel = getPanelElement()
      if (!panel) {
        logger.warn('PopupCoordinator: Panel element not found')
        return
      }
      
      
      // Wait for CSS animation to complete
      const animationTimer = setTimeout(() => {
        if (pendingPopup.marker) {
          const topPadding = calculateTopPadding(panel)
          const popup = pendingPopup.marker.getPopup()
          
          
          if (popup) {
            popup.options.autoPanPaddingTopLeft = L.point(20, topPadding)
            popup.options.autoPanPaddingBottomRight = L.point(20, 20)
            popup.options.keepInView = true
            popup.options.autoPan = true
          }
          
          pendingPopup.marker.openPopup()
          setActivePopup(pendingPopup.marker)
          
          // Wait for popup to fully render, then position using actual DOM position
          setTimeout(() => {
            if (!pendingPopup.marker._map) return
            
            const map = pendingPopup.marker._map
            const popupElement = popup?.getElement()
            
            if (popupElement) {
              // Use actual rendered position from DOM (not calculated from marker)
              const popupRect = popupElement.getBoundingClientRect()
              const currentPopupTop = popupRect.top
              
              // Calculate pan needed to reach exact target position
              const panDelta = currentPopupTop - topPadding
              
              if (Math.abs(panDelta) > 5) { // Only pan if difference is significant
                const currentCenter = map.getCenter()
                const currentCenterPoint = map.latLngToContainerPoint(currentCenter)
                const newCenterPoint = L.point(currentCenterPoint.x, currentCenterPoint.y + panDelta)
                const newCenter = map.containerPointToLatLng(newCenterPoint)
                
                map.panTo(newCenter, { animate: true, duration: 0.25 })
              }
            }
            
            setPendingPopup(null)
          }, 100) // Wait for popup to fully render
        }
      }, 350)
      
      return () => clearTimeout(animationTimer)
    }
    
    // Panel state not minimized yet - this shouldn't happen but keep for safety
  }, [pendingPopup, panelState])
  
  /**
   * Effect: Update popup position when panel state changes
   * Ensures popup remains visible when panel expands/contracts
   */
  useEffect(() => {
    if (!activePopup) return
    
    const popup = activePopup.getPopup()
    if (!popup || !popup.isOpen()) return
    
    const updatePopupPosition = () => {
      // Re-verify popup still open after animation delay
      if (!activePopup?.getPopup()?.isOpen()) return
      
      const panel = getPanelElement()
      const topPadding = calculateTopPadding(panel)
      const map = activePopup._map
      
      if (!map) return
      
      // Update popup padding options
      popup.options.autoPanPaddingTopLeft = L.point(20, topPadding)
      popup.options.autoPanPaddingBottomRight = L.point(20, 20)
      
      // Check if popup needs repositioning
      const popupElement = popup.getElement()
      if (popupElement) {
        const popupRect = popupElement.getBoundingClientRect()
        const currentTop = popupRect.top
        
        // If popup is behind panel, pan map to reveal it
        if (currentTop < topPadding) {
          const panAmount = topPadding - currentTop
          map.panBy([0, -panAmount], { animate: true, duration: 0.3 })
        }
      }
    }
    
    // Wait for animations: MapBoundsFitter (~300ms) + panel transition (300ms) + buffer
    const timer = setTimeout(updatePopupPosition, 700)
    
    return () => clearTimeout(timer)
  }, [activePopup, panelState])

  
  /**
   * Close the currently active popup
   */
  const closePopup = useCallback(() => {
    if (activePopup) {
      activePopup.closePopup()
      setActivePopup(null)
    }
    setPendingPopup(null)
  }, [activePopup])
  
  return {
    openPopup,
    closePopup,
    activePopup,
    isPending: !!pendingPopup,
    popupPadding
  }
}
